import { Component, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { FlatpickrDatePickerComponent } from '../../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { MaintenanceCostingService } from '../../../services/maintenance-costing.service';
import { AddAncillaryCostRequest, UploadTempInvoiceRequest } from '../../../models/maintenance-costing-request.model';
import { Subject } from 'rxjs';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { AuthService } from '../../../../../../_shared/services/auth.service';
import { CurrencyHelper } from '../../../../../../_shared/helpers/currency/currency-map.helper';
import { AccountAssetScopeSelectorComponent, ScopeSelection } from '../../../../../../_shared/components';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { parseLocaleNumber } from '../../../../../../_shared/helpers/number-input.helper';
import { toApiDate } from '../../../helpers/picker-date.helper';
import {
  INVOICE_ACCEPTED_TYPES,
  INVOICE_MAX_FILE_NAME_LENGTH,
  INVOICE_MAX_SIZE_BYTES,
  INVOICE_MAX_SIZE_MB,
  INVOICE_MEDIA_TYPES
} from '../../../constants/invoice-upload.constants';

@Component({
  selector: 'app-add-invoice-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, FlatpickrDatePickerComponent, AccountAssetScopeSelectorComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './add-invoice-modal.component.html',
  styleUrl: './add-invoice-modal.component.scss'
})
export class AddInvoiceModalComponent implements OnDestroy {
  private activeModal: NgbActiveModal = inject(NgbActiveModal);
  private fb: FormBuilder = inject(FormBuilder);
  private maintenanceCostingService: MaintenanceCostingService = inject(MaintenanceCostingService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private toastr: ToastrService = inject(ToastrService);
  private readonly transloco: TranslocoService = inject(TranslocoService);
  private authService: AuthService = inject(AuthService);

  readonly acceptedInvoiceTypes: string = INVOICE_ACCEPTED_TYPES;

  invoiceForm: FormGroup;
  currencySymbol: string = '';
  selectedAsset: ScopeSelection | null = null;
  date: string = '';
  isSaving: boolean = false;
  isUploadingInvoice: boolean = false;
  invoiceFileName: string = '';

  private invoiceReference: string = '';
  private invoiceExtension: string = '';
  private cancelSubject: Subject<void> = new Subject<void>();
  private invoiceCancelSubject: Subject<void> = new Subject<void>();

  constructor() {
    const currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(currencyCode);

    this.invoiceForm = this.fb.group({
      category: ['', Validators.required],
      description: [''],
      invoiceNumber: [''],
      cost: ['', Validators.required],
      odometer: ['', Validators.required],
      notes: ['']
    });
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
    this.invoiceCancelSubject.next();
    this.invoiceCancelSubject.complete();
  }

  onScopeChange(scope: ScopeSelection | null): void {
    const previousAssetId = this.selectedAssetId;
    this.selectedAsset = scope;

    if (previousAssetId === this.selectedAssetId) {
      return;
    }

    if (this.invoiceFileName || this.isUploadingInvoice) {
      this.invoiceCancelSubject.next();
      this.clearInvoice();
      this.toastr.info(this.translate('invoiceCleared'));
    }
  }

  get selectedAssetId(): number | null {
    return this.selectedAsset?.type === 'asset' ? this.selectedAsset.id : null;
  }

  get canSave(): boolean {
    return !this.isSaving && !this.isUploadingInvoice && this.isFormValid;
  }

  get isFormValid(): boolean {
    return !!(
      this.selectedAsset &&
      this.date &&
      this.trimmed('category') &&
      this.cost !== null &&
      this.odometer !== null
    );
  }

  async onInvoiceSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';

    if (!file || this.selectedAssetId === null) {
      return;
    }

    const extension = this.getExtension(file.name);
    const mediaType = INVOICE_MEDIA_TYPES[extension];

    if (!mediaType) {
      this.toastr.error(this.translate('unsupportedType'));
      return;
    }

    if (file.size > INVOICE_MAX_SIZE_BYTES) {
      this.toastr.error(this.translate('tooLarge', { size: INVOICE_MAX_SIZE_MB }));
      return;
    }

    if (file.name.length > INVOICE_MAX_FILE_NAME_LENGTH) {
      this.toastr.error(this.translate('fileNameTooLong', { length: INVOICE_MAX_FILE_NAME_LENGTH }));
      return;
    }

    await this.uploadInvoice(file, extension, mediaType);
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  async save(): Promise<void> {
    if (!this.canSave) {
      return;
    }

    this.isSaving = true;

    const request: AddAncillaryCostRequest = {
      assetId: this.selectedAsset!.id,
      date: toApiDate(this.date),
      category: this.trimmed('category'),
      description: this.trimmed('description'),
      cost: this.cost!,
      odometer: this.odometer!,
      invoiceNumber: this.trimmed('invoiceNumber'),
      notes: this.trimmed('notes'),
      // Sending the reference is what moves the uploaded file out of temporary storage
      invoice: this.invoiceReference
        ? { reference: this.invoiceReference, fileName: this.invoiceFileName, extension: this.invoiceExtension }
        : null
    };

    try {
      await this.maintenanceCostingService.AddAncillaryCostRequest(request, this.cancelSubject);
      this.isSaving = false;
      this.toastr.success(this.translate('added'));
      this.activeModal.close(true);
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isSaving = false;
        }
      });
    }
  }

  private async uploadInvoice(file: File, extension: string, mediaType: string): Promise<void> {
    this.isUploadingInvoice = true;

    const request: UploadTempInvoiceRequest = {
      assetId: this.selectedAssetId!,
      reference: crypto.randomUUID(),
      extension,
      mediaType,
      invoice: file
    };

    try {
      await this.maintenanceCostingService.UploadTempRequest(request, this.invoiceCancelSubject);

      this.invoiceReference = request.reference;
      this.invoiceExtension = extension;
      this.invoiceFileName = file.name;
      this.isUploadingInvoice = false;
    } catch (error) {
      // Reset before handling: a cancelled upload throws the EmptyError that handleError returns early on
      this.clearInvoice();
      this.errorHandlerService.handleError(error, { showToast: true });
    }
  }

  private clearInvoice(): void {
    this.isUploadingInvoice = false;
    this.invoiceFileName = '';
    this.invoiceReference = '';
    this.invoiceExtension = '';
  }

  private getExtension(fileName: string): string {
    const separatorIndex = fileName.lastIndexOf('.');
    return separatorIndex === -1 ? '' : fileName.slice(separatorIndex + 1).toLowerCase();
  }

  private get cost(): number | null {
    return this.parseAmount('cost');
  }

  private get odometer(): number | null {
    return this.parseAmount('odometer');
  }

  private parseAmount(controlName: string): number | null {
    const value = parseLocaleNumber(this.invoiceForm.get(controlName)?.value, this.transloco.getActiveLang());
    return value !== null && value >= 0 ? value : null;
  }

  private trimmed(controlName: string): string {
    return (this.invoiceForm.get(controlName)?.value ?? '').trim();
  }

  private translate(toastKey: string, params?: Record<string, unknown>): string {
    return this.transloco.translate(`maintenanceCosting.modals.addInvoice.toasts.${toastKey}`, params);
  }
}